import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { clearRequestCache } from '@/lib/supabaseUtils';
import { clearTenantIdCache } from '@/utils/tenantUtils';
import { useAuth } from './AuthContext';
import { getDomainTenant, getDefaultTenant } from '@/services/domainService';

interface Tenant {
  tenant_id: string;
  company_name: string;
  contact_name: string;
  contact_phone?: string | null;
  contact_email: string;
  created_at: string;
  updated_at: string;
}

interface TenantContextType {
  currentTenant: Tenant | null;
  userTenants: Tenant[];
  switchTenant: (tenantId: string) => Promise<void>;
  returnToHomeTenant: () => Promise<void>;
  getCurrentTenantId: () => Promise<string | null>;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  addTenant: (tenant: Tenant) => void;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

// Global cache for tenant data to prevent duplicate requests
const tenantCache = new Map<string, {
  tenant: Tenant | null;
  timestamp: number;
  promise?: Promise<any>;
}>();

// Global cache for tenant ID to prevent repeated database calls
const tenantIdCache = new Map<string, {
  tenantId: string | null;
  timestamp: number;
  promise?: Promise<string | null>;
}>();

// Cache expiration times
const TENANT_CACHE_EXPIRY = 5 * 60 * 1000; // 5 minutes for tenant data
const TENANT_ID_CACHE_EXPIRY = 10 * 60 * 1000; // 10 minutes for tenant ID

// Exponential backoff for retrying failed requests
const retryWithBackoff = async function <T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<T> {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error: any) {
      const isLastAttempt = attempt === maxRetries - 1;
      const is522Error = error?.status === 522 || error?.code === 522;
      
      if (isLastAttempt || !is522Error) {
        throw error;
      }
      
      // Exponential backoff for 522 errors
      const delay = baseDelay * Math.pow(2, attempt);
      console.warn(`[TenantContext] 522 error on attempt ${attempt + 1}, retrying in ${delay}ms`);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
  
  throw new Error('Max retries exceeded');
};

// Deduplicated tenant request function
const deduplicatedTenantRequest = async (
  cacheKey: string,
  requestFn: () => Promise<any>
): Promise<any> => {
  const cached = tenantCache.get(cacheKey);
  const now = Date.now();

  // Return cached data if it's still valid
  if (cached && now - cached.timestamp < TENANT_CACHE_EXPIRY) {
    return cached.tenant;
  }

  // If there's an ongoing request, wait for it
  if (cached?.promise) {
    return await cached.promise;
  }

  // Make the request and cache the promise
  const promise = requestFn();
  tenantCache.set(cacheKey, {
    tenant: null,
    timestamp: now,
    promise
  });

  try {
    const result = await promise;
    tenantCache.set(cacheKey, {
      tenant: result,
      timestamp: now
    });
    return result;
  } catch (error) {
    // Remove failed request from cache
    tenantCache.delete(cacheKey);
    throw error;
  }
};

// Deduplicated tenant ID request function
const deduplicatedTenantIdRequest = async (
  cacheKey: string,
  requestFn: () => Promise<string | null>
): Promise<string | null> => {
  const cached = tenantIdCache.get(cacheKey);
  const now = Date.now();

  // Return cached data if it's still valid
  if (cached && now - cached.timestamp < TENANT_ID_CACHE_EXPIRY) {
    return cached.tenantId;
  }

  // If there's an ongoing request, wait for it
  if (cached?.promise) {
    return await cached.promise;
  }

  // Make the request and cache the promise
  const promise = requestFn();
  tenantIdCache.set(cacheKey, {
    tenantId: null,
    timestamp: now,
    promise
  });

  try {
    const result = await promise;
    tenantIdCache.set(cacheKey, {
      tenantId: result,
      timestamp: now
    });
    return result;
  } catch (error) {
    // Remove failed request from cache
    tenantIdCache.delete(cacheKey);
    throw error;
  }
};

export function TenantProvider({ children }: { children: React.ReactNode }) {
  const { user, authLoading } = useAuth();
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null);
  const [userTenants, setUserTenants] = useState<Tenant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userHasSystemOverride, setUserHasSystemOverride] = useState(false);
  
  const abortControllerRef = useRef<AbortController>(new AbortController());

  // Clear cache when user changes
  useEffect(() => {
    tenantCache.clear();
    tenantIdCache.clear();
    clearTenantIdCache(); // Clear tenant ID cache from tenantUtils.ts
    clearRequestCache(); // Clear request cache when user changes
    abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
  }, [user?.id]);

  const getCurrentTenantId = async (): Promise<string | null> => {
    if (!user) return null;

    const cacheKey = `tenant-id-${user.id}`;
    
    return await deduplicatedTenantIdRequest(cacheKey, async () => {
      return await retryWithBackoff(async () => {
        const { data, error } = await supabase.rpc('get_current_user_tenant_id');
        
        if (error) {
          console.error('[TenantContext] Error getting current tenant ID:', error);
          return null;
        }

        return data;
      });
    });
  };

  const fetchCurrentTenant = async () => {
    if (!user) {
      setCurrentTenant(null);
      return;
    }

    const signal = abortControllerRef.current.signal;

    try {
      const cacheKey = `current-tenant-${user.id}`;
      
      const tenant = await deduplicatedTenantRequest(cacheKey, async () => {
        return await retryWithBackoff(async () => {
          const tenantId = await getCurrentTenantId();
          
          if (!tenantId) {
            return null;
          }

          // Get tenant details
          const { data: tenantData, error: tenantError } = await supabase
            .from('tenants')
            .select('*')
            .eq('tenant_id', tenantId)
            .single();

          if (tenantError) {
            console.error('[TenantContext] Error fetching tenant details:', tenantError);
            throw tenantError;
          }

          return tenantData;
        });
      });

      if (signal.aborted) return;
      setCurrentTenant(tenant);
    } catch (error: any) {
      if (signal.aborted) return;
      console.error('[TenantContext] Error fetching current tenant:', error);
      setError(error.message);
    }
  };

  // Check if user has system.override permission
  const checkSystemOverridePermission = async (): Promise<boolean> => {
    if (!user) {
      setUserHasSystemOverride(false);
      return false;
    }

    try {
      // First check the legacy user's primary role
      const { data: userData } = await supabase
        .from('users')
        .select(`
          role_id,
          roles!inner (
            global,
            name
          )
        `)
        .eq('id', user.id)
        .single();

      // Handle the case where roles might be an array (though it should be single object)
      const userRole = Array.isArray(userData?.roles) ? userData.roles[0] : userData?.roles;
      
      let hasOverride = false;
      
      // Check legacy system first
      if (userRole?.name === 'Superadmin' || userData?.role_id === 42) {
        hasOverride = true;
      } else if (userData?.role_id) {
        // Check role_permissions for system.override in legacy system
        // Use the role's tenant_id for permission lookup
        const { data: roleData } = await supabase
          .from('roles')
          .select('global, tenant_id')
          .eq('id', userData?.role_id)
          .single();
        
        // Use role's tenant_id if available, otherwise get current tenant
        const effectiveTenantId = roleData?.tenant_id || await getCurrentTenantId();
        
        const { data: permissionCheck } = await supabase
          .from('role_permissions')
          .select(`
            permissions!inner (name)
          `)
          .eq('role_id', userData?.role_id)
          .eq('tenant_id', effectiveTenantId)
          .eq('permissions.name', 'system.override')
          .limit(1);

        hasOverride = Boolean(permissionCheck && permissionCheck.length > 0);
      }

      // If not found in legacy system, check tenant-specific user_roles
      if (!hasOverride) {
        // Check all tenant-specific roles for this user
        const { data: userRolesData } = await supabase
          .from('user_roles')
          .select(`
            role_id,
            roles!inner (
              name,
              global
            )
          `)
          .eq('user_id', user.id);

        if (userRolesData && userRolesData.length > 0) {
          // Check if any role is Superadmin or has system.override permission
          for (const userRoleData of userRolesData) {
            const role = Array.isArray(userRoleData.roles) ? userRoleData.roles[0] : userRoleData.roles;
            
            if (role?.name === 'Superadmin') {
              hasOverride = true;
              break;
            }

            // Check role_permissions for system.override
            // Use the role's tenant_id for permission lookup
            const { data: roleDetailsData } = await supabase
              .from('roles')
              .select('global, tenant_id')
              .eq('id', userRoleData.role_id)
              .single();
            
            // Use role's tenant_id if available
            const effectiveTenantId = roleDetailsData?.tenant_id;
            
            if (effectiveTenantId) {
              const { data: rolePermissionCheck } = await supabase
                .from('role_permissions')
                .select(`
                  permissions!inner (name)
                `)
                .eq('role_id', userRoleData.role_id)
                .eq('tenant_id', effectiveTenantId)
                .eq('permissions.name', 'system.override')
                .limit(1);

              if (rolePermissionCheck && rolePermissionCheck.length > 0) {
                hasOverride = true;
                break;
              }
            }
          }
        }
      }
      
      setUserHasSystemOverride(hasOverride);
      return hasOverride;
    } catch (error) {
      console.error('[TenantContext] Error checking system.override permission:', error);
      setUserHasSystemOverride(false);
      return false;
    }
  };

  const fetchUserTenants = async (hasSystemOverride?: boolean) => {
    if (!user) {
      setUserTenants([]);
      return;
    }

    const signal = abortControllerRef.current.signal;
    const systemOverride = hasSystemOverride !== undefined ? hasSystemOverride : userHasSystemOverride;

    try {
      // If user has system.override permission, give access to all tenants (excluding archived)
      if (systemOverride) {
        const { data: allTenants, error: allTenantsError } = await supabase
          .from('tenants')
          .select('*')
          .or('is_archived.is.null,is_archived.eq.false')
          .order('company_name');

        if (allTenantsError) {
          console.error('[TenantContext] Error fetching all tenants for superadmin:', allTenantsError);
          return;
        }

        setUserTenants(allTenants || []);
        return;
      }

      // For non-superadmin users, use the regular user_roles lookup
      const { data: userRoles, error: userRolesError } = await supabase
        .from('user_roles')
        .select(`
          tenant_id,
          roles!inner(name)
        `)
        .eq('user_id', user.id);

      if (userRolesError) {
        console.error('[TenantContext] Error fetching user roles:', userRolesError);
        return;
      }

      if (!userRoles || userRoles.length === 0) {
        setUserTenants([]);
        return;
      }

      // Get unique tenant IDs from user roles
      const tenantIds = [...new Set(userRoles.map(ur => ur.tenant_id))];

      // Fetch tenant details for all accessible tenants (excluding archived)
      const { data: tenantData, error: tenantError } = await supabase
        .from('tenants')
        .select('*')
        .in('tenant_id', tenantIds)
        .or('is_archived.is.null,is_archived.eq.false');

      if (tenantError) {
        console.error('[TenantContext] Error fetching tenant details:', tenantError);
        return;
      }

      setUserTenants(tenantData || []);
      
    } catch (error: any) {
      if (signal.aborted) return;
      console.error('[TenantContext] Error fetching user tenants:', error);
      // Don't set error here as it's not critical for core functionality
    }
  };

  const switchTenant = async (tenantId: string) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      // Verify user has access to this tenant
      const hasAccess = userTenants.some(tenant => tenant.tenant_id === tenantId);
      if (!hasAccess) {
        throw new Error('User does not have access to this tenant');
      }

      // Set the superadmin's active tenant session
      const { error } = await supabase
        .rpc('set_superadmin_active_tenant', { p_tenant_id: tenantId });

      if (error) {
        console.error('[TenantContext] Error setting superadmin active tenant:', error);
        throw error;
      }

      // Clear all caches to force refresh on next page load
      tenantCache.clear();
      tenantIdCache.clear();
      clearTenantIdCache();
      clearRequestCache();

      // Note: We don't update state here because the page will reload immediately
      // This prevents unnecessary re-renders and refetches before the reload
    } catch (error: any) {
      console.error('[TenantContext] Error switching tenant:', error);
      throw error;
    }
  };

  const returnToHomeTenant = async () => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      // Clear the superadmin's active tenant session
      const { data, error } = await supabase
        .rpc('clear_superadmin_active_tenant');

      if (error) {
        console.error('[TenantContext] Error clearing superadmin active tenant:', error);
        throw error;
      }

      // Refresh the tenant data to get the home tenant
      await refetch();
    } catch (error: any) {
      console.error('[TenantContext] Error returning to home tenant:', error);
      throw error;
    }
  };

  const refetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // First check permissions, then fetch tenant data sequentially
      // Pass the permission result directly to avoid state timing issues
      const hasSystemOverride = await checkSystemOverridePermission();

      await fetchCurrentTenant();

      await fetchUserTenants(hasSystemOverride);
    } catch (error: any) {
      console.error('[TenantContext] Error in refetch:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  const addTenant = useCallback((tenant: Tenant) => {
    // Add the new tenant to the list and sort alphabetically
    setUserTenants(prev => {
      const exists = prev.some(t => t.tenant_id === tenant.tenant_id);
      if (exists) return prev;

      const updated = [...prev, tenant];
      return updated.sort((a, b) => a.company_name.localeCompare(b.company_name));
    });
  }, []);

  // Get default tenant for unauthenticated domain-based detection
  const getDefaultTenantForDomain = async () => {
    try {
      const domainTenant = await getDomainTenant();
      if (domainTenant) {
        // Convert domain tenant to our Tenant interface
        return {
          tenant_id: domainTenant.tenantId,
          company_name: domainTenant.tenantName,
          contact_name: '',
          contact_phone: '',
          contact_email: '',
          created_at: '',
          updated_at: ''
        } as Tenant;
      }
    } catch (error) {
      console.error('Error getting domain tenant:', error);
    }
    
    // Fallback to default tenant
    const defaultTenant = getDefaultTenant();
    return {
      tenant_id: defaultTenant.tenantId,
      company_name: defaultTenant.tenantName,
      contact_name: '',
      contact_phone: '',
      contact_email: '',
      created_at: '',
      updated_at: ''
    } as Tenant;
  };

  // Fetch tenant data when user changes
  useEffect(() => {
    if (user && !authLoading) {
      refetch();
    } else if (!user && !authLoading) {
      // Only run domain-based tenant lookup for truly unauthenticated users
      // Don't run if auth is still loading (prevents tab switch triggers)
      setIsLoading(true);
      getDefaultTenantForDomain().then(domainTenant => {
        setCurrentTenant(domainTenant);
        setUserTenants([]);
        setIsLoading(false);
        setError(null);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  const value: TenantContextType = useMemo(() => ({
    currentTenant,
    userTenants,
    switchTenant,
    returnToHomeTenant,
    getCurrentTenantId,
    isLoading,
    error,
    refetch,
    addTenant
  }), [currentTenant, userTenants, switchTenant, returnToHomeTenant, getCurrentTenantId, isLoading, error, refetch, addTenant]);

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant(): TenantContextType {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}
